import { lazy, Suspense } from 'react';
import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import SearchFilter from '@alltrails/shared/components/SearchFilter';
import useModalPortalElement from '@alltrails/shared/hooks/useModalPortalElement';
import User from '@alltrails/shared/types/User';
import useFiltersContext from 'hooks/useFiltersContext';
import { FilterViewModel, FilterConfig, FilterInput, FilterType } from 'types/Search';
import { SearchFiltersUtil } from 'utils/search_filters_util';
import getSaveButtonChildrenProps from '../saveButtonChildrenProps';

const FilterInputFactory = lazy(() => import('components/explore/filters/FilterInputFactory'));

export const getPillLabel = (filter: FilterViewModel, activeFilters: string[]) => {
  if (activeFilters.length === 0) {
    return filter.display;
  }

  switch (filter.type) {
    case FilterInput.RADIO:
      if (activeFilters.length === 1) {
        return activeFilters[0];
      }
      break;
    case FilterInput.CHECKBOX:
      if (activeFilters.length === 1) {
        return `${activeFilters[0]}`;
      }
      return `${activeFilters[0]} • ${activeFilters.length - 1}`;
    case FilterInput.RANGE:
      if (activeFilters.length === 1) {
        const displayFunction = SearchFiltersUtil.getFilterDisplayFunction(filter.config.displayFunction);

        const min = filter.config.formattedRange[0];
        const max = filter.config.formattedRange[1];

        return `${displayFunction(min)} - ${displayFunction(max)}`;
      }
      break;
    case FilterInput.SLIDER:
      if (activeFilters.length === 1) {
        const displayFunction = SearchFiltersUtil.getFilterDisplayFunction(filter.config.displayFunction);

        return <FormattedMessage defaultMessage="Within {distance}" values={{ distance: displayFunction(filter.config.formattedRange) }} />;
      }
      break;
    default:
      break;
  }

  return filter.display;
};

export type Props = {
  filter: FilterViewModel;
  popperClassName?: string;
  user?: User;
};

const getSubText = (filterKey: string) => {
  if (filterKey === FilterType.DISTANCE_AWAY) {
    return <FormattedMessage defaultMessage="See trails based on your current location" />;
  }
  return null;
};

const FilterPillFactory = ({ filter, popperClassName, user }: Props) => {
  const intl = useIntl();

  const {
    appliedFilterLabelsByKey,
    clearAppliedFilters,
    clearDraftChanges,
    computedResultsCount,
    filtersByKey,
    isLoadingHypotheticalResultsCount,
    resetHypotheticalResultsCount,
    saveChanges,
    updateFilter
  } = useFiltersContext();

  const isActive = appliedFilterLabelsByKey[filter.key]?.length > 0;
  const pillLabel = getPillLabel(filter, appliedFilterLabelsByKey[filter.key] || []);

  const modalElement = useModalPortalElement();

  return (
    <SearchFilter
      clearButtonHandler={() => {
        clearAppliedFilters([filter.key]);
        resetHypotheticalResultsCount();
      }}
      hasActiveFilters={isActive}
      modalElement={modalElement}
      modalTitle={filter.display}
      modalDescription={getSubText(filter.key)}
      pillButtonText={pillLabel}
      popperClassName={popperClassName}
      resetCurrentFormStateHandler={() => {
        clearDraftChanges();
        resetHypotheticalResultsCount();
      }}
      saveButtonChildren={getSaveButtonChildrenProps({
        intl,
        isLoading: isLoadingHypotheticalResultsCount,
        resultsCount: computedResultsCount,
        disabled:
          (user === null && filter.enabledKey === FilterType.DISTANCE_AWAY) || (user && !user.pro && filter.enabledKey === FilterType.DISTANCE_AWAY)
      })}
      saveButtonHandler={saveChanges}
      hasPlus={filter.isProFilter}
    >
      <Suspense fallback={null}>
        <FilterInputFactory filter={filtersByKey[filter.key]} onChange={(changes: FilterConfig) => updateFilter(filter.key, changes)} user={user} />
      </Suspense>
    </SearchFilter>
  );
};

export default FilterPillFactory;
